/**
 * Determines whether a city uses sq ft or m² for commercial property,
 * and provides conversion helpers.
 */

const SQFT_COUNTRIES = new Set(["US", "UK", "CA"]);

const CITY_COUNTRY: Record<string, string> = {
  miami: "US", "new york": "US", "los angeles": "US", chicago: "US",
  houston: "US", austin: "US", nashville: "US",
  london: "UK", manchester: "UK", edinburgh: "UK",
  toronto: "CA", vancouver: "CA",
  madrid: "ES", barcelona: "ES", valencia: "ES",
  milan: "IT", rome: "IT", florence: "IT",
  dubai: "AE", "abu dhabi": "AE",
  sydney: "AU", melbourne: "AU", brisbane: "AU",
  singapore: "SG",
  "são paulo": "BR", "sao paulo": "BR",
  paris: "FR", lyon: "FR", marseille: "FR", nice: "FR",
  berlin: "DE", munich: "DE", hamburg: "DE", frankfurt: "DE",
  amsterdam: "NL", rotterdam: "NL",
  brussels: "BE", antwerp: "BE",
  vienna: "AT", salzburg: "AT",
  lisbon: "PT", porto: "PT",
  tokyo: "JP", osaka: "JP", kyoto: "JP",
  "mexico city": "MX", "ciudad de méxico": "MX", cancun: "MX", "cancún": "MX", guadalajara: "MX",
  zurich: "CH", "zürich": "CH", geneva: "CH", basel: "CH",
};

const SQFT_TO_M2 = 0.0929;

export function getCityCountry(city: string): string {
  const key = city.toLowerCase().trim();
  return CITY_COUNTRY[key] || "US";
}

export function usesSqft(city: string): boolean {
  return SQFT_COUNTRIES.has(getCityCountry(city));
}

export function areaUnit(city: string): string {
  return usesSqft(city) ? "sq ft" : "m²";
}

export function areaUnitShort(city: string): string {
  return usesSqft(city) ? "sqft" : "m²";
}

/** Convert sq ft value to the appropriate unit for the city. Always stores internally as sq ft. */
export function convertArea(sqft: number, city: string): number {
  if (usesSqft(city)) return sqft;
  return Math.round(sqft * SQFT_TO_M2);
}

/** Format an area range string like "800–1,500 sq ft" or "74–139 m²" */
export function formatAreaRange(minSqft: number, maxSqft: number, city: string): string {
  const unit = areaUnit(city);
  const min = convertArea(minSqft, city);
  const max = convertArea(maxSqft, city);
  return `${min.toLocaleString()}–${max.toLocaleString()} ${unit}`;
}

/** Per-seat display */
export function formatAreaPerSeat(sqftPerSeat: number, city: string): string {
  const val = convertArea(sqftPerSeat, city);
  return `${val} ${areaUnit(city)}`;
}

/** Size option labels for onboarding */
export function getSizeOptions(city: string) {
  const u = areaUnit(city);
  const isSqft = usesSqft(city);
  return [
    { id: "500-1000", label: isSqft ? `500 – 1,000 ${u}` : `46 – 93 ${u}`, desc: "Small, cozy space" },
    { id: "1000-2000", label: isSqft ? `1,000 – 2,000 ${u}` : `93 – 186 ${u}`, desc: "Standard size" },
    { id: "2000-4000", label: isSqft ? `2,000 – 4,000 ${u}` : `186 – 372 ${u}`, desc: "Spacious venue" },
    { id: "4000-plus", label: isSqft ? `4,000+ ${u}` : `372+ ${u}`, desc: "Flagship / large format" },
  ];
}

/** Cost calculator size labels */
export function getCostCalcSizes(city: string) {
  const u = areaUnitShort(city);
  const isSqft = usesSqft(city);
  return [
    { value: "500-1000" as const, label: isSqft ? `500 – 1,000 ${u}` : `46 – 93 ${u}`, midpoint: 750 },
    { value: "1000-2000" as const, label: isSqft ? `1,000 – 2,000 ${u}` : `93 – 186 ${u}`, midpoint: 1500 },
    { value: "2000-4000" as const, label: isSqft ? `2,000 – 4,000 ${u}` : `186 – 372 ${u}`, midpoint: 3000 },
  ];
}
